/**
 * useAnalytics — v3.99bb
 *
 * Fire-and-forget pageview logger. Mounted ONCE inside the router so
 * every route change emits a single POST /api/public/analytics/pageview.
 *
 * Why a router-mounted hook (not per-page useEffect):
 *   - Guarantees a pageview event fires exactly once per navigation,
 *     including hash/search-only changes if we ever care, and back/forward.
 *   - Single network request per route change — never coupled to component
 *     mount timings (so a route that mounts 4 sub-components doesn't fire
 *     4 pageviews).
 *
 * Privacy / safety:
 *   - Path is normalized server-side (blocklists `/admin`, `/vendor`,
 *     `/account`, `/checkout`, `/field-access`, `/login`, `/register`).
 *   - Sends ONLY pathname (no query string, no hash).
 *   - Sends `document.referrer` — server reduces it to a host.
 *   - Includes `eval_mode` boolean so the admin can segment AI/evaluator
 *     traffic from real user traffic.
 *   - Soft-fails silently on network errors so analytics never breaks
 *     navigation.
 */
import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { api } from "./apiClient";
import { isEvalModeActive } from "./useEvalMode";

export default function useAnalytics() {
  const location = useLocation();
  const lastPath = useRef(null);

  useEffect(() => {
    const path = location.pathname;
    if (!path || lastPath.current === path) return;
    lastPath.current = path;
    // Debounce slightly so the redirect chain on Field Access activation
    // doesn't double-fire.
    const t = setTimeout(() => {
      api.post("/public/analytics/pageview", {
        path,
        referrer: document.referrer || "",
        eval_mode: isEvalModeActive(),
      }).catch(() => { /* swallow */ });
    }, 80);
    return () => clearTimeout(t);
  }, [location.pathname]);
}
